import * as React from 'react'
import { useEffect } from 'react'
import { AsyncStatus, useAsync } from '@react-hook/async'
import { useOktaAuth } from '@okta/okta-react'

export interface Carrier {
  aplIndicator: boolean
  carrierAccountCode: string
  carrierCode: string
  carrierGroupAccountCode: string
  carrierName: string
  hubCode: string
  xDockScacCodes: string
}

export type ContextType = {
  status: AsyncStatus
  value: Carrier[]
  error: Error[]
  cancel: () => void
}

const CarriersContext = React.createContext<ContextType>(undefined)

const fetchCarriers = async (accessToken?: string): Promise<any> => {
  const baseUrl = process.env.REACT_APP_CARRIER_CONFIG_API_URL
  const response = await fetch(`${baseUrl}/carriers/v1`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
  })

  if (response.ok) {
    return response.json()
  } else {
    const msg = `Response status: ${response.status}. Response body: ${response.body}`
    throw new Error(msg)
  }
}

export function CarriersProvider(props) {
  const { authState } = useOktaAuth()
  const accessToken = authState?.accessToken?.accessToken
  const [state, doFetch] = useAsync(async () => fetchCarriers(accessToken))
  useEffect(() => {
    if (!accessToken) {
      return
    }
    if (state.status === 'idle' || state.status === 'cancelled') {
      doFetch()
    }
  }, [state, doFetch, accessToken])
  return <CarriersContext.Provider value={state} {...props} />
}

export function useCarriers(): [Carrier[], boolean, boolean] {
  const context = React.useContext(CarriersContext)
  if (!context) {
    throw new Error('useCarriers must be used within a CarriersContext')
  }
  return [context.value, context.status === 'loading', context.status === 'error']
}

export const IsCarriersLoading = () => {
  return useCarriers()[1]
}

export function useCarrier(vendorCode: string, hub: string): [Carrier, boolean, boolean] {
  const [carriers, loading, error] = useCarriers()
  return [
    carriers?.find((c) => c.carrierAccountCode === vendorCode && (!hub || c.hubCode === hub)),
    loading,
    error,
  ]
}
